import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { automationService } from '@/lib/automationService';
import { getCurrentUserOrganizationId } from '@/lib/data.js';
import { supabase } from '@/lib/supabaseClient';
import { Pencil, Trash2, Plus, Settings, Users, Calendar, Heart, Zap } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const categoryIcons = {
  visitor_management: Users,
  member_engagement: Heart,
  event_management: Calendar,
  pastoral_care: Heart,
  default: Zap
};

const categoryColors = {
  visitor_management: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  member_engagement: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  event_management: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  pastoral_care: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  default: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
};

export default function AutomationTemplates() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [templateUsage, setTemplateUsage] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] = useState(false);
  const [customizingTemplate, setCustomizingTemplate] = useState(null);
  const [customizationForm, setCustomizationForm] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const orgId = await getCurrentUserOrganizationId();
        setOrganizationId(orgId);
        
        if (orgId) {
          await Promise.all([
            loadTemplates(),
            loadTemplateUsage(orgId),
            loadCategories(),
            loadUsers(orgId)
          ]);
        }
      } catch (error) {
        console.error('Error initializing automation templates:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await automationService.getAutomationTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadTemplateUsage = async (orgId) => {
    try {
      const data = await automationService.getTemplateUsage(orgId);
      setTemplateUsage(data);
    } catch (error) {
      console.error('Error loading template usage:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await automationService.getTemplateCategories();
      setCategories(['all', ...data]);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadUsers = async (orgId) => {
    try {
      console.log('Loading users for organization:', orgId);
      
      // Get organization users with member data
      const [orgUsersResult, membersResult] = await Promise.all([
        supabase
          .from('organization_users')
          .select('user_id, role, approval_status')
          .eq('organization_id', orgId)
          .eq('status', 'active')
          .eq('approval_status', 'approved')
          .order('created_at', { ascending: false }),
        supabase
          .from('members')
          .select('id, firstname, lastname, email, user_id')
          .eq('organization_id', orgId)
          .order('firstname', { ascending: true })
      ]);

      if (orgUsersResult.error) throw orgUsersResult.error;
      if (membersResult.error) throw membersResult.error;

      // Create a map of user_id to member data
      const membersMap = new Map();
      membersResult.data?.forEach(member => {
        if (member.user_id) {
          membersMap.set(member.user_id, member);
        }
      });

      // Transform to user list for assignment
      const userList = [];
      orgUsersResult.data?.forEach(orgUser => {
        const member = membersMap.get(orgUser.user_id);
        if (member) {
          userList.push({
            id: member.user_id,
            name: `${member.firstname} ${member.lastname}`,
            email: member.email,
            role: orgUser.role
          });
        }
      });

      console.log('Users loaded:', userList);
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const isTemplateActive = (templateId) => {
    return templateUsage.some(usage => usage.template_id === templateId && usage.is_enabled);
  };

  const getTemplateUsage = (templateId) => {
    return templateUsage.find(usage => usage.template_id === templateId);
  };

  const handleActivateTemplate = async (template) => {
    if (!organizationId) return;

    try {
      await automationService.activateTemplate(organizationId, template.id);
      await loadTemplateUsage(organizationId);
      
      toast({
        title: "Template Activated",
        description: `"${template.name}" has been activated for your organization.`
      });
    } catch (error) {
      console.error('Error activating template:', error);
      toast({
        title: "Error",
        description: "Failed to activate template. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeactivateTemplate = async (template) => {
    if (!organizationId) return;

    try {
      await automationService.deactivateTemplate(organizationId, template.id);
      await loadTemplateUsage(organizationId);
      
      toast({
        title: "Template Deactivated",
        description: `"${template.name}" has been deactivated.`
      });
    } catch (error) {
      console.error('Error deactivating template:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate template. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCustomizeTemplate = (template) => {
    const usage = getTemplateUsage(template.id);
    setCustomizingTemplate(template);
    
    // Initialize form with current customizations or template defaults
    const customizations = usage?.customizations || {};
    setCustomizationForm({
      name: customizations.name || template.name,
      description: customizations.description || template.description,
      trigger_conditions: customizations.trigger_conditions || template.trigger_conditions,
      action_data: customizations.action_data || template.action_data,
      is_active: customizations.is_active !== undefined ? customizations.is_active : true
    });
    
    setIsCustomizeDialogOpen(true);
  };

  const handleSaveCustomizations = async () => {
    if (!organizationId || !customizingTemplate) return;

    try {
      const isActive = isTemplateActive(customizingTemplate.id);
      
      if (isActive) {
        // Update existing customizations
        await automationService.updateTemplateCustomizations(
          organizationId, 
          customizingTemplate.id, 
          customizationForm
        );
      } else {
        // Activate template with customizations
        await automationService.activateTemplate(
          organizationId, 
          customizingTemplate.id, 
          customizationForm
        );
      }
      
      await loadTemplateUsage(organizationId);
      setIsCustomizeDialogOpen(false);
      
      toast({
        title: "Template Updated",
        description: `"${customizingTemplate.name}" has been updated with your customizations.`
      });
    } catch (error) {
      console.error('Error saving customizations:', error);
      toast({
        title: "Error",
        description: "Failed to save customizations. Please try again.",
        variant: "destructive"
      });
    }
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const formatCategoryName = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Automation Templates</CardTitle>
            <CardDescription>Loading available automation templates...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Automation Templates</CardTitle>
            <CardDescription>Activate and customize automation templates</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-4">
              Unable to load automation templates. Please ensure you are associated with an organization.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Automation Templates
          </CardTitle>
          <CardDescription>
            Activate and customize pre-built automation templates for your church. 
            Each template can be enabled and tailored to your specific needs.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Category Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const isSelected = selectedCategory === category;
              return (
                <Button
                  key={category}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="capitalize"
                >
                  {category === 'all' ? 'All Templates' : formatCategoryName(category)}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredTemplates.map((template) => {
          const isActive = isTemplateActive(template.id);
          const usage = getTemplateUsage(template.id);
          const IconComponent = categoryIcons[template.category] || categoryIcons.default;
          const categoryColor = categoryColors[template.category] || categoryColors.default;

          return (
            <Card key={template.id} className={`relative ${isActive ? 'ring-2 ring-green-500' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <IconComponent className="h-5 w-5 text-gray-600" />
                    <Badge className={categoryColor}>
                      {formatCategoryName(template.category)}
                    </Badge>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleActivateTemplate(template);
                      } else {
                        handleDeactivateTemplate(template);
                      }
                    }}
                  />
                </div>
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <CardDescription className="text-sm">
                  {template.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1">
                  {template.tags?.map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Trigger:</strong> {template.trigger_type}</div>
                  <div><strong>Action:</strong> {template.action_type}</div>
                </div>

                {isActive && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCustomizeTemplate(template)}
                      className="w-full"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Customize
                    </Button>
                    {usage?.is_customized && (
                      <Badge variant="outline" className="mt-2 text-xs">
                        Customized
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-gray-500">No templates found in this category.</p>
          </CardContent>
        </Card>
      )}

      {/* Customization Dialog */}
      <Dialog open={isCustomizeDialogOpen} onOpenChange={setIsCustomizeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customize Template: {customizingTemplate?.name}</DialogTitle>
            <DialogDescription>
              Modify this template to fit your church's specific needs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={customizationForm.name || ''}
                onChange={(e) => setCustomizationForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter custom name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={customizationForm.description || ''}
                onChange={(e) => setCustomizationForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter custom description"
                rows={2}
              />
            </div>

            {/* User-friendly Trigger Conditions */}
            <div className="space-y-2 border-t pt-4 mt-4">
              <Label>Trigger Conditions</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <Select
                    value={customizationForm.trigger_conditions?.event_type || 'any'}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      trigger_conditions: { 
                        ...prev.trigger_conditions, 
                        event_type: v === 'any' ? undefined : v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="Worship Service">Worship Service</SelectItem>
                      <SelectItem value="Bible Study or Class">Bible Study or Class</SelectItem>
                      <SelectItem value="Fellowship Gathering">Fellowship Gathering</SelectItem>
                      <SelectItem value="Children's Ministry">Children's Ministry</SelectItem>
                      <SelectItem value="Youth Group">Youth Group</SelectItem>
                      <SelectItem value="Prayer Meeting">Prayer Meeting</SelectItem>
                      <SelectItem value="Special Event">Special Event</SelectItem>
                      <SelectItem value="Community Service">Community Service</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Member Type</Label>
                  <Select
                    value={customizationForm.trigger_conditions?.member_type || 'any'}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      trigger_conditions: { 
                        ...prev.trigger_conditions, 
                        member_type: v === 'any' ? undefined : v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="visitor">Visitor</SelectItem>
                      <SelectItem value="adult">Adult</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Attendance Status</Label>
                  <Select
                    value={customizationForm.trigger_conditions?.attendance_status || 'any'}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      trigger_conditions: { 
                        ...prev.trigger_conditions, 
                        attendance_status: v === 'any' ? undefined : v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="attended">Attended</SelectItem>
                      <SelectItem value="not_attending">Not Attending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Is First Visit</Label>
                  <Select
                    value={customizationForm.trigger_conditions?.is_first_visit === undefined ? 'any' : String(customizationForm.trigger_conditions.is_first_visit)}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      trigger_conditions: { 
                        ...prev.trigger_conditions, 
                        is_first_visit: v === 'any' ? undefined : v === 'true' 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Member Status</Label>
                  <Select
                    value={customizationForm.trigger_conditions?.status || 'any'}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      trigger_conditions: { 
                        ...prev.trigger_conditions, 
                        status: v === 'any' ? undefined : v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="visitor">Visitor</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* User-friendly Action Data */}
            <div className="space-y-2 border-t pt-4 mt-4">
              <Label>Action Data</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Task Title</Label>
                  <Input
                    value={customizationForm.action_data?.title || ''}
                    onChange={e => setCustomizationForm(prev => ({ 
                      ...prev, 
                      action_data: { 
                        ...prev.action_data, 
                        title: e.target.value 
                      } 
                    }))}
                    placeholder="e.g. Follow up with {member_name}"
                  />
                  <div className="mt-1 text-xs text-gray-500">
                    <span>Available: </span>
                    <code className="bg-gray-100 px-1 rounded">{'{member_name}'}</code>
                    <code className="bg-gray-100 px-1 rounded">{'{event_date_formatted}'}</code>
                    <code className="bg-gray-100 px-1 rounded">{'{phone}'}</code>
                  </div>
                </div>
                <div>
                  <Label>Task Description</Label>
                  <Textarea
                    value={customizationForm.action_data?.description || ''}
                    onChange={e => setCustomizationForm(prev => ({ 
                      ...prev, 
                      action_data: { 
                        ...prev.action_data, 
                        description: e.target.value 
                      } 
                    }))}
                    placeholder="e.g. New visitor {member_name} attended..."
                    rows={4}
                  />
                  <div className="mt-2 text-xs text-gray-500">
                    <p className="font-medium mb-1">Available variables:</p>
                    <div className="grid grid-cols-2 gap-1">
                      <code className="bg-gray-100 px-1 rounded">{'{member_name}'}</code>
                      <code className="bg-gray-100 px-1 rounded">{'{event_date}'}</code>
                      <code className="bg-gray-100 px-1 rounded">{'{event_date_formatted}'}</code>
                      <code className="bg-gray-100 px-1 rounded">{'{phone}'}</code>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={customizationForm.action_data?.priority || 'medium'}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      action_data: { 
                        ...prev.action_data, 
                        priority: v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Medium" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned To</Label>
                  <Select
                    value={customizationForm.action_data?.assigned_to || ''}
                    onValueChange={v => setCustomizationForm(prev => ({ 
                      ...prev, 
                      action_data: { 
                        ...prev.action_data, 
                        assigned_to: v 
                      } 
                    }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {users.length > 0 ? (
                        users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.role})
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-users" disabled>
                          No users available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Due Date Offset (days)</Label>
                  <Input
                    type="number"
                    value={customizationForm.action_data?.due_date_offset_days || ''}
                    onChange={e => setCustomizationForm(prev => ({ 
                      ...prev, 
                      action_data: { 
                        ...prev.action_data, 
                        due_date_offset_days: Number(e.target.value) 
                      } 
                    }))}
                    placeholder="e.g. 3"
                  />
                </div>
              </div>
            </div>

            {/* Advanced: Raw JSON fallback for power users */}
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-gray-500">Advanced: Edit raw JSON</summary>
              <div className="space-y-2 mt-2">
                <Label>Trigger Conditions (JSON)</Label>
                <Textarea
                  value={JSON.stringify(customizationForm.trigger_conditions || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setCustomizationForm(prev => ({ ...prev, trigger_conditions: parsed }));
                    } catch (error) {
                      // Invalid JSON, don't update
                    }
                  }}
                  rows={6}
                  className="font-mono text-sm"
                />
                <Label>Action Data (JSON)</Label>
                <Textarea
                  value={JSON.stringify(customizationForm.action_data || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setCustomizationForm(prev => ({ ...prev, action_data: parsed }));
                    } catch (error) {
                      // Invalid JSON, don't update
                    }
                  }}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
            </details>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={customizationForm.is_active}
                onCheckedChange={(checked) => setCustomizationForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCustomizeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCustomizations}>
              Save Customizations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
